import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

const EASE_OUT = [0.22, 0.8, 0.2, 1] as const
const CURTAIN = [0.76, 0, 0.24, 1] as const
const DURATION = 1700

export default function SiteLoader({ onComplete }: { onComplete: () => void }) {
    const [progress, setProgress] = useState(0)

    useEffect(() => {
        let raf = 0
        let start = 0
        const tick = (now: number) => {
            if (!start) start = now
            const t = Math.min(1, (now - start) / DURATION)
            const eased = 1 - Math.pow(1 - t, 3)
            setProgress(Math.round(eased * 100))
            if (t < 1) raf = requestAnimationFrame(tick)
            else raf = window.setTimeout(onComplete, 360) as unknown as number
        }
        raf = requestAnimationFrame(tick)
        return () => {
            cancelAnimationFrame(raf)
            clearTimeout(raf)
        }
    }, [onComplete])

    return (
        <motion.div
            role="status"
            aria-label="Loading Indian Studies Review"
            className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
            style={{
                background:
                    'radial-gradient(1100px 760px at 50% 24%, #ffffff 0%, #fbf7ef 52%, #f3ecd9 100%)'
            }}
            initial={{ opacity: 1 }}
            exit={{ y: '-100%' }}
            transition={{ duration: 0.85, ease: CURTAIN }}
        >
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(620px 460px at 86% -12%, rgba(158,43,37,0.10), transparent 60%), radial-gradient(520px 420px at 6% 112%, rgba(158,43,37,0.07), transparent 60%)'
                }}
            />

            <div className="relative flex flex-col items-center text-center px-8">
                <motion.img
                    src="/brand/cspg-logo.png"
                    alt="Centre for Study of Politics and Governance"
                    className="w-auto"
                    style={{ height: 'clamp(56px, 9vw, 76px)' }}
                    initial={{ opacity: 0, scale: 0.94, y: 6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE_OUT }}
                />

                <motion.div
                    className="flex items-center gap-2.5 mt-7"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                >
                    <span style={{ width: 38, height: 1, background: 'rgba(13,11,8,0.18)' }} />
                    <span style={{ width: 5, height: 5, transform: 'rotate(45deg)', background: 'var(--color-gold-500)' }} />
                    <span style={{ width: 38, height: 1, background: 'rgba(13,11,8,0.18)' }} />
                </motion.div>

                <div className="overflow-hidden mt-5">
                    <motion.h1
                        className="font-display"
                        style={{
                            fontSize: 'clamp(1.75rem, 6vw, 2.6rem)',
                            lineHeight: 1.04,
                            letterSpacing: '-0.02em',
                            color: 'var(--color-ink-700)',
                            fontWeight: 500
                        }}
                        initial={{ y: '110%' }}
                        animate={{ y: '0%' }}
                        transition={{ duration: 0.8, delay: 0.18, ease: EASE_OUT }}
                    >
                        <em style={{ fontStyle: 'italic', color: 'var(--color-gold-600)' }}>Indian</em> Studies Review
                    </motion.h1>
                </div>

                <motion.div
                    className="mt-3.5 text-[9.5px] uppercase font-semibold"
                    style={{ letterSpacing: '0.34em', color: 'var(--color-ink-400)' }}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    Centre for Study of Politics &amp; Governance
                </motion.div>

                <motion.div
                    className="mt-9 flex flex-col items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                >
                    <div
                        className="relative overflow-hidden"
                        style={{ width: 'min(230px, 64vw)', height: 2, background: 'rgba(13,11,8,0.08)', borderRadius: 2 }}
                    >
                        <motion.div
                            className="absolute inset-y-0 left-0"
                            style={{
                                width: '100%',
                                transformOrigin: 'left',
                                background:
                                    'linear-gradient(90deg, var(--color-gold-600), var(--color-gold-400))',
                                borderRadius: 2
                            }}
                            animate={{ scaleX: progress / 100 }}
                            transition={{ ease: 'linear', duration: 0.1 }}
                        />
                    </div>
                    <div
                        className="mt-3 tabular-nums"
                        style={{ fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', color: 'var(--color-ink-400)' }}
                    >
                        {String(progress).padStart(2, '0')}%
                    </div>
                </motion.div>
            </div>
        </motion.div>
    )
}
